// src/dnd/emitter_tool/io/exportImport.js

const PANEL_OPEN_KEY_PREFIX = "EmitterTool:";

// SpritePanel persistence keys
const SPRITE_MODE_KEY  = "EmitterTool:SpritePanel:ModeV1";
const SPRITE_MULTI_KEY = "EmitterTool:SpritePanel:LocalMultiV1";
const SPRITE_ANIM_KEY  = "EmitterTool:SpritePanel:LocalAnimFramesV1";
const SPRITE_FPS_KEY   = "EmitterTool:SpritePanel:AnimFPSV1";

function deepClone(v) {
  return JSON.parse(JSON.stringify(v));
}

export function parseMaybeModuleText(text) {
  const raw = String(text || "").trim();
  try {
    const j = JSON.parse(raw);
    if (j && typeof j === "object") return j;
  } catch {}
  const m = raw.match(/export\s+default\s+(\{[\s\S]*\})\s*;?\s*$/m);
  if (!m) throw new Error("Could not parse file. Expect JSON or `export default { ... }`.");
  let objStr = m[1].replace(/,\s*}/g, "}").replace(/,\s*]/g, "]");
  return JSON.parse(objStr);
}

function collectLocalStorageSnapshot() {
  const out = {};
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PANEL_OPEN_KEY_PREFIX)) continue;
      out[k] = localStorage.getItem(k);
    }
  } catch {}
  return out;
}

function restoreLocalStorageSnapshot(map) {
  if (!map || typeof map !== "object") return;
  try {
    Object.entries(map).forEach(([k, v]) => {
      if (k && k.startsWith(PANEL_OPEN_KEY_PREFIX)) {
        localStorage.setItem(k, String(v));
      }
    });
  } catch {}
}

export function buildExportObject({
  name,
  effectKey,
  pxPerMeter,
  showGrid,
  ui,
  parent,
  layers,
  includeLocalStorage = true,
}) {
  const data = {
    name: name || "",
    effectKey: effectKey || "",
    pxPerMeter: Number(pxPerMeter || 0),
    showGrid: !!showGrid,
    parent: deepClone({
      selectedSprite: parent?.selectedSprite ?? null,
      effectSettings: parent?.effectSettings ?? {},
      emitterShape: parent?.emitterShape ?? "point",
      shapeSettings: parent?.shapeSettings ?? {},
      infinite: !!parent?.infinite,
      infiniteRate: Number.isFinite(+parent?.infiniteRate) ? +parent.infiniteRate : 0,
    }),
    layers: Array.isArray(layers) ? deepClone(layers) : [],
  };

  if (ui && typeof ui === "object") {
    data.ui = {
      showGrid: typeof ui.showGrid === "boolean" ? ui.showGrid : !!showGrid,
      wallsEnabled: !!ui.wallsEnabled,
      tokenEnabled: !!ui.tokenEnabled,
      fireMode: ui.fireMode || undefined,
      timedDur: Number.isFinite(+ui.timedDur) ? +ui.timedDur : undefined,
      panelOpen: ui.panelOpen && typeof ui.panelOpen === "object" ? { ...ui.panelOpen } : undefined,
    };
  }

  if (includeLocalStorage) {
    data.localStorage = collectLocalStorageSnapshot();
  }

  return data;
}

export function serializeExportObject(data, pretty = true) {
  const json = JSON.stringify(data, null, pretty ? 2 : 0);
  return `// Exported from EmitterTool\nexport default ${json};\n`;
}

export function normalizeSpriteFields(parentObj = {}, effectObj = {}) {
  const out = { ...(effectObj || {}) };

  const fromParentArr = parentObj.selectedSprites;
  const fromEffectArr = effectObj.selectedSprites;
  const fromParentSingle = parentObj.selectedSprite;
  const fromEffectSingle = effectObj.selectedSprite;

  let finalArr =
    Array.isArray(fromParentArr) && fromParentArr.length ? fromParentArr
    : Array.isArray(fromEffectArr) && fromEffectArr.length ? fromEffectArr
    : null;

  if (!finalArr) {
    const single =
      typeof fromParentSingle === "string" ? fromParentSingle
      : typeof fromEffectSingle === "string" ? fromEffectSingle
      : null;
    if (single) finalArr = [single];
  }

  if (finalArr) {
    out.selectedSprites = Array.isArray(finalArr) ? finalArr.slice() : [finalArr];
    out.selectedSprite = out.selectedSprites[0] || null;
  }

  const mode = (effectObj.spriteMode || parentObj.spriteMode || "single").toLowerCase();
  if (mode === "single") {
    out.spriteMode = "single";
    const first = out.selectedSprite || out.selectedSprites?.[0] || null;
    out.selectedSprites = first ? [first] : [];
    out.selectedSprite = first;
    out.selectedAnimFrames = [];
    out.spriteAnimatedEnabled = false;
  } else if (mode === "multi") {
    out.spriteMode = "multi";
    out.selectedSprites = Array.isArray(out.selectedSprites) ? out.selectedSprites : [];
    out.selectedAnimFrames = [];
    out.spriteAnimatedEnabled = false;
  } else if (mode === "anim") {
    out.spriteMode = "anim";
    out.selectedSprites = Array.isArray(out.selectedSprites) ? out.selectedSprites : [];
    out.selectedAnimFrames = Array.isArray(out.selectedAnimFrames) ? out.selectedAnimFrames : [];
    out.spriteAnimatedEnabled = out.selectedAnimFrames.length >= 2;
    if (!Number.isFinite(+out.animFps)) out.animFps = 12;
  } else {
    out.spriteMode = "single";
  }

  return out;
}

function writeSpritePanelLocalStorage(normalizedEffect, parentObj = {}) {
  try {
    const mode = (normalizedEffect.spriteMode || "single").toLowerCase();
    const first =
      normalizedEffect.selectedSprite ||
      normalizedEffect.selectedSprites?.[0] ||
      parentObj.selectedSprite ||
      null;

    localStorage.setItem(SPRITE_MODE_KEY, mode);

    if (mode === "single") {
      localStorage.setItem(SPRITE_MULTI_KEY, JSON.stringify(first ? [first] : []));
      localStorage.setItem(SPRITE_ANIM_KEY, JSON.stringify([]));
      localStorage.setItem(SPRITE_FPS_KEY, String(Number(normalizedEffect.animFps || 12)));
    } else if (mode === "multi") {
      localStorage.setItem(
        SPRITE_MULTI_KEY,
        JSON.stringify(Array.isArray(normalizedEffect.selectedSprites) ? normalizedEffect.selectedSprites : [])
      );
      localStorage.setItem(SPRITE_ANIM_KEY, JSON.stringify([]));
      localStorage.setItem(SPRITE_FPS_KEY, String(Number(normalizedEffect.animFps || 12)));
    } else if (mode === "anim") {
      localStorage.setItem(
        SPRITE_MULTI_KEY,
        JSON.stringify(Array.isArray(normalizedEffect.selectedSprites) ? normalizedEffect.selectedSprites : [])
      );
      localStorage.setItem(
        SPRITE_ANIM_KEY,
        JSON.stringify(Array.isArray(normalizedEffect.selectedAnimFrames) ? normalizedEffect.selectedAnimFrames : [])
      );
      localStorage.setItem(SPRITE_FPS_KEY, String(Number(normalizedEffect.animFps || 12)));
    }
  } catch {}
}

export function applyImportObject({
  data,
  flushSync,
  setters,
  runtimeKick,
}) {
  if (!data || typeof data !== "object") throw new Error("Malformed preset");

  const {
    setEffectName,
    setPxPerMeter,
    setShowGrid,
    setEffectKey,
    setWallsEnabled,
    setTokenEnabled,
    setEmitterShape,
    setShapeSettings,
    setEffectSettings,
    setSelectedSprite,
    setLayers,
    setActiveLayerIndex,
    setPanelOpenMapToLocalStorage,
  } = setters || {};

  const ui = data.ui || {};
  const parent = data.parent || {};
  const incomingEffect =
    parent.effectSettings && typeof parent.effectSettings === "object"
      ? parent.effectSettings
      : {};

  const normalizedEffect = normalizeSpriteFields(parent, incomingEffect);

  if (data.localStorage && typeof data.localStorage === "object") {
    restoreLocalStorageSnapshot(data.localStorage);
  }

  writeSpritePanelLocalStorage(normalizedEffect, parent);

  if (typeof setPanelOpenMapToLocalStorage === "function" && ui.panelOpen) {
    setPanelOpenMapToLocalStorage(ui.panelOpen);
  }

  flushSync(() => {
    if (typeof data.name === "string") setEffectName?.(data.name);
    if (Number.isFinite(+data.pxPerMeter)) setPxPerMeter?.(+data.pxPerMeter);
    if (typeof data.showGrid === "boolean") setShowGrid?.(!!data.showGrid);
    if (typeof data.effectKey === "string") setEffectKey?.(data.effectKey);

    if (typeof ui.showGrid === "boolean") setShowGrid?.(!!ui.showGrid);
    if (typeof ui.wallsEnabled === "boolean") setWallsEnabled?.(!!ui.wallsEnabled);
    if (typeof ui.tokenEnabled === "boolean") setTokenEnabled?.(!!ui.tokenEnabled);

    setEffectSettings?.({
      ...normalizedEffect,
      __version: (normalizedEffect.__version || 0) + 1,
    });

    if (typeof parent.selectedSprite === "string") {
      setSelectedSprite?.(parent.selectedSprite);
    } else if (
      Array.isArray(normalizedEffect.selectedSprites) &&
      normalizedEffect.selectedSprites.length
    ) {
      setSelectedSprite?.(normalizedEffect.selectedSprites[0]);
    } else {
      setSelectedSprite?.(null);
    }

    if (typeof parent.emitterShape === "string") setEmitterShape?.(parent.emitterShape);
    if (parent.shapeSettings && typeof parent.shapeSettings === "object") {
      setShapeSettings?.(deepClone(parent.shapeSettings));
    }

    if (typeof parent.infinite === "boolean") {
      setEffectSettings?.((es) => ({ ...(es || {}), infinite: !!parent.infinite }));
    }
    if (Number.isFinite(+parent.infiniteRate)) {
      setEffectSettings?.((es) => ({ ...(es || {}), infiniteRate: +parent.infiniteRate }));
    }

    if (Array.isArray(data.layers)) {
      setLayers?.(data.layers);
      setActiveLayerIndex?.(-1);
    }
  });

  runtimeKick?.({
    from: "import",
    parent: {
      emitterShape: parent.emitterShape,
      shapeSettings: parent.shapeSettings,
      effectSettings: normalizedEffect,
      selectedSprite:
        normalizedEffect.selectedSprite ||
        normalizedEffect.selectedSprites?.[0] ||
        parent.selectedSprite ||
        null,
      infinite: parent.infinite,
      infiniteRate: parent.infiniteRate,
    },
    top: {
      name: data.name,
      pxPerMeter: data.pxPerMeter ?? ui.pxPerMeter,
      showGrid: typeof data.showGrid === "boolean" ? data.showGrid : ui.showGrid,
      effectKey: data.effectKey,
      wallsEnabled: ui.wallsEnabled,
      tokenEnabled: ui.tokenEnabled,
    },
    layers: data.layers,
  });
}

function parseExportModuleText(text) {
  const raw = String(text || "").trim();
  try {
    const j = JSON.parse(raw);
    if (j && typeof j === "object" && !Array.isArray(j)) return j;
  } catch {}
  const m = raw.match(/export\s+default\s+(\{[\s\S]*\})\s*;?\s*$/);
  if (!m) throw new Error("Could not find an exportable object in the file.");
  let objStr = m[1];
  objStr = objStr.replace(/,\s*}/g, "}").replace(/,\s*]/g, "]");
  return JSON.parse(objStr);
}

function emit(name, detail) {
  try {
    window.dispatchEvent(new CustomEvent(name, { detail }));
  } catch {}
}

function emitSubEmitterRules(arr, reason) {
  const now = performance.now();
  for (const child of Array.isArray(arr) ? arr : []) {
    if (!child || !child.id) continue;
    emit("EmitterTool:SubEmitterRule", {
      ts: now,
      reason: reason || "import.hydrate",
      id: child.id,
      name: child.name || "",
      enabled: child.enabled !== false,
      startMode: child.startMode || "immediate",
      delayMs: Number(child.delayMs) || 0,
      runForMode: child.runForMode || "inherit",
      runDurationS: Number(child.runDurationS) || 0.5,
    });
  }
}

function derivedSpriteMode(es, header) {
  const h = header || {};
  const m = (h.mode || es?.spriteMode || "").toLowerCase();
  if (m === "single" || m === "multi" || m === "anim") return m;
  const frames = Array.isArray(h.selectedAnimFrames)
    ? h.selectedAnimFrames
    : Array.isArray(es?.selectedAnimFrames)
    ? es.selectedAnimFrames
    : [];
  const multi = Array.isArray(h.selectedSprites)
    ? h.selectedSprites
    : Array.isArray(es?.selectedSprites)
    ? es.selectedSprites
    : [];
  if (frames.length >= 2) return "anim";
  if (multi.length > 1) return "multi";
  return "single";
}

function pickTrailPatch(src) {
  if (!src || typeof src !== "object") return {};
  const out = {};
  const num = (v) => (Number.isFinite(+v) ? +v : undefined);
  const str = (v) => (typeof v === "string" ? v : undefined);
  const bool = (v) => (typeof v === "boolean" ? v : undefined);
  const map = {
    trailEnabled: (v) => (bool(v) != null ? !!v : undefined),
    trailMode: (v) => str(v),
    trailLength: (v) =>
      num(v) != null ? Math.max(1, Math.floor(num(v))) : undefined,
    trailSpacingMs: (v) =>
      num(v) != null ? Math.max(0, Math.floor(num(v))) : undefined,
    trailAlphaStart: (v) =>
      num(v) != null ? Math.max(0, Math.min(1, num(v))) : undefined,
    trailAlphaEnd: (v) =>
      num(v) != null ? Math.max(0, Math.min(1, num(v))) : undefined,
    trailScaleStart: (v) =>
      num(v) != null ? Math.max(0, num(v)) : undefined,
    trailScaleEnd: (v) =>
      num(v) != null ? Math.max(0, num(v)) : undefined,
    ribbonWidthPx: (v) =>
      num(v) != null ? Math.max(0, Math.floor(num(v))) : undefined,
    trailBlendMode: (v) => str(v),
    trailUseSpriteSource: (v) =>
      bool(v) != null ? !!v : undefined,
    trailTextureKey: (v) => str(v),
    subTrailPps: (v) =>
      num(v) != null ? Math.max(0, Math.floor(num(v))) : undefined,
    subTrailLifeS: (v) =>
      num(v) != null ? Math.max(0.01, num(v)) : undefined,
  };
  for (const k of Object.keys(map)) {
    const v = map[k](src[k]);
    if (v !== undefined) out[k] = v;
  }
  return out;
}

function bestTrailSource(data) {
  const hasTrailKeys =
    data?.ParticlesPanel &&
    Object.keys(data.ParticlesPanel).some(
      (k) => k.startsWith("trail") || k === "ribbonWidthPx"
    );

  return (
    data?.TrailPanel ||
    data?.TrailsPanel ||
    data?.TrailesPanel ||
    data?.Trailes ||
    data?.parent?.effectSettings ||
    (hasTrailKeys ? data.ParticlesPanel : null) ||
    null
  );
}

export function coerceSnapshotForLegacyImport(data) {
  if (!data || typeof data !== "object") return data;
  if (!data.meta || typeof data.meta !== "object") return data;

  const out = { ...data };
  const meta = out.meta || {};
  const srcES =
    meta.effectSettings && typeof meta.effectSettings === "object"
      ? { ...meta.effectSettings }
      : {};

  const existingParent =
    out.parent && typeof out.parent === "object" ? out.parent : {};

  const parentES = {
    ...(existingParent.effectSettings || {}),
    ...srcES,
  };

  const parent = {
    ...existingParent,
    emitterShape:
      meta.emitterShape ||
      existingParent.emitterShape ||
      parentES.emitterShape ||
      "point",
    shapeSettings: {
      ...(existingParent.shapeSettings || {}),
      ...(meta.shapeSettings || {}),
    },
    effectSettings: parentES,
    selectedSprite:
      meta.selectedSprite ??
      parentES.selectedSprite ??
      existingParent.selectedSprite ??
      null,
  };

  if (typeof meta.showShape === "boolean") {
    parent.showShape = !!meta.showShape;
  }

  if (out.TargetingPanel && typeof out.TargetingPanel === "object") {
    const tp = out.TargetingPanel;
    parent.targeting = {
      mode: (tp.presetId || tp.mode || "global").toString().toLowerCase(),
      rangeM: tp.rangeM,
      headSpeedMps: tp.headSpeedMps,
    };
  }

  const trailPanel =
    (out.TrailPanel &&
      typeof out.TrailPanel === "object" &&
      out.TrailPanel) ||
    (out.TrailsPanel &&
      typeof out.TrailsPanel === "object" &&
      out.TrailsPanel) ||
    null;

  if (trailPanel) {
    const trailPatch = pickTrailPatch(trailPanel);
    if (Object.keys(trailPatch).length) {
      parent.effectSettings = {
        ...(parent.effectSettings || {}),
        ...trailPatch,
      };
    }
  }

  // NEW: map TimingPanel -> EmitterPanel.rule so the existing import path
  // will hydrate fireMode / ruleStyle / duration into localStorage + events.
  if (out.TimingPanel && typeof out.TimingPanel === "object") {
    const tp = out.TimingPanel;
    const fireMode = String(tp.fireMode || "infinite").toLowerCase();
    const ruleStyle = String(tp.ruleStyle || "infinite").toLowerCase();
    const durRaw = Number(tp.timedDur);
    const ruleDurationS = Number.isFinite(durRaw) && durRaw > 0 ? durRaw : 2;

    out.EmitterPanel = out.EmitterPanel || {};
    out.EmitterPanel.rule = {
      ...(out.EmitterPanel.rule || {}),
      fireMode,
      ruleStyle,
      ruleDurationS,
    };
  }

  out.parent = parent;

  delete out.TargetingPanel;
  delete out.TrailPanel;
  delete out.TrailsPanel;
  delete out.TrailesPanel;
  delete out.Trailes;
  delete out.TimingPanel;

  return out;
}

export function applyImportedEffectObject(data, env) {
  if (!data || typeof data !== "object")
    throw new Error("Malformed preset.");
  if (!env || typeof env !== "object")
    throw new Error("Missing env (state setters).");
  const {
    effects,
    effectKey,
    setEffectKey,
    setEffectName,
    setSelectedSprite,
    setEffectSettings,
    setEmitterShape,
    setShapeSettings,
    setInfinite,
    setInfiniteRate,
    setLayers,
    setShowGrid,
    setPxPerMeter,
    setWallsEnabled,
    setTokenEnabled,
    emitterShape,
    selectedSprite,
    effectSettings,
    setActiveLayerIndex,
    setShowShape,
  } = env;

  let finalES =
    effectSettings && typeof effectSettings === "object"
      ? { ...effectSettings }
      : {};
  const mergeES = (patch) => {
    const p = typeof patch === "function" ? patch(finalES) : patch || {};
    finalES = { ...finalES, ...p };
    if (typeof setEffectSettings === "function") {
      setEffectSettings((cur) => ({ ...(cur || {}), ...p }));
    }
    return finalES;
  };

  let usedPanelHeaders = false;

  if (data.EmitterPanel && typeof data.EmitterPanel === "object") {
    usedPanelHeaders = true;
    const ep = data.EmitterPanel;
    if (typeof data.name === "string") setEffectName?.(data.name);
    if (Number.isFinite(+data.pxPerMeter))
      setPxPerMeter?.(+data.pxPerMeter);
    if (typeof data.showGrid === "boolean")
      setShowGrid?.(!!data.showGrid);
    if (
      typeof data.effectKey === "string" &&
      effects?.[data.effectKey]
    ) {
      setEffectKey?.(data.effectKey);
    }
    if (typeof ep.shape === "string") setEmitterShape?.(ep.shape);
    if (typeof ep.showShape === "boolean")
      setShowShape?.(!!ep.showShape);
    if (ep.settings && typeof ep.settings === "object") {
      setShapeSettings?.(ep.settings);
    }
    if (ep.rule && typeof ep.rule === "object") {
      const fm = String(ep.rule.fireMode || "infinite").toLowerCase();
      const rs = String(ep.rule.ruleStyle || "infinite").toLowerCase();
      const rd = Number.isFinite(ep.rule.ruleDurationS)
        ? ep.rule.ruleDurationS
        : 2;
      try {
        localStorage.setItem("EmitterTool:UI:fireMode", fm);
      } catch {}
      try {
        localStorage.setItem("EmitterTool:UI:ruleStyle", rs);
      } catch {}
      try {
        localStorage.setItem("EmitterTool:UI:ruleDur", String(rd));
      } catch {}
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:ModeChange")
        );
        window.dispatchEvent(
          new CustomEvent("EmitterTool:RuleConfig", {
            detail: { ruleStyle: rs, ruleDurationS: rd },
          })
        );
      } catch {}
    }
  }

  if (data.TargetingPanel && typeof data.TargetingPanel === "object") {
    usedPanelHeaders = true;
    const tp = data.TargetingPanel;
    const presetId = (tp.presetId || tp.mode || "global")
      .toString()
      .toLowerCase();
    const rangeM = Number(tp.rangeM);
    const headSpeedMps = Number(tp.headSpeedMps);
    try {
      localStorage.setItem(
        "EmitterTool:targeting:mode",
        presetId
      );
    } catch {}
    if (Number.isFinite(rangeM)) {
      try {
        localStorage.setItem(
          "EmitterTool:targeting:rangeM",
          String(rangeM)
        );
      } catch {}
    }
    if (Number.isFinite(headSpeedMps)) {
      try {
        localStorage.setItem(
          "EmitterTool:targeting:headSpeedMps",
          String(headSpeedMps)
        );
      } catch {}
    }
    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:Targeting:Hydrate", {
          detail: {
            mode: presetId,
            rangeM: Number.isFinite(rangeM) ? rangeM : undefined,
            headSpeedMps: Number.isFinite(headSpeedMps)
              ? headSpeedMps
              : undefined,
            targeting: { mode: presetId, rangeM, headSpeedMps },
          },
        })
      );
    } catch {}
  }

  if (data.SpritePanel && typeof data.SpritePanel === "object") {
    usedPanelHeaders = true;
    const sp = data.SpritePanel;
    const baseES =
      env?.effectSettings && typeof env.effectSettings === "object"
        ? { ...env.effectSettings }
        : { ...finalES };
    const hdrSprites = Array.isArray(sp.selectedSprites)
      ? sp.selectedSprites.slice()
      : [];
    const hdrFrames = Array.isArray(sp.selectedAnimFrames)
      ? sp.selectedAnimFrames.slice()
      : [];
    const hdrSingle =
      typeof sp.selectedSprite === "string"
        ? sp.selectedSprite
        : hdrSprites[0] ?? baseES.selectedSprite ?? null;
    const mode = derivedSpriteMode(baseES, sp);
    const nextES = { ...baseES };
    if (mode === "single") {
      const singleName =
        hdrSingle ||
        (Array.isArray(baseES.selectedSprites) &&
          baseES.selectedSprites[0]) ||
        baseES.selectedSprite ||
        null;
      nextES.spriteMode = "single";
      nextES.selectedSprite = singleName || null;
      nextES.selectedSprites = singleName ? [singleName] : [];
      nextES.selectedAnimFrames = [];
      nextES.spriteAnimatedEnabled = false;
    } else if (mode === "multi") {
      const arr = hdrSprites.length
        ? hdrSprites
        : Array.isArray(baseES.selectedSprites)
        ? baseES.selectedSprites
        : [];
      nextES.spriteMode = "multi";
      nextES.selectedSprites = arr.slice();
      nextES.selectedSprite = arr[0] ?? null;
      nextES.selectedAnimFrames = [];
      nextES.spriteAnimatedEnabled = false;
    } else if (mode === "anim") {
      const frames = hdrFrames.length
        ? hdrFrames
        : Array.isArray(baseES.selectedAnimFrames)
        ? baseES.selectedAnimFrames
        : [];
      nextES.spriteMode = "anim";
      nextES.selectedAnimFrames = frames.slice();
      nextES.spriteAnimatedEnabled = frames.length >= 2;
      const fps = Number.isFinite(+sp.animFps)
        ? +sp.animFps
        : Number.isFinite(+baseES.animFps)
        ? +baseES.animFps
        : 12;
      nextES.animFps = fps;
      nextES.selectedSprite =
        frames[0] ?? nextES.selectedSprite ?? (Array.isArray(nextES.selectedSprites) &&
          nextES.selectedSprites[0]) ??
        null;
      if (
        !Array.isArray(nextES.selectedSprites) ||
        !nextES.selectedSprites.length
      ) {
        nextES.selectedSprites = nextES.selectedSprite
          ? [nextES.selectedSprite]
          : [];
      }
    }
    mergeES(nextES);
    try {
      emit("EmitterTool:SpritePanel:SetMode", { mode });
      if (mode === "single") {
        const nm =
          finalES.selectedSprite ||
          (Array.isArray(finalES.selectedSprites) &&
            finalES.selectedSprites[0]) ||
          null;
        if (nm)
          emit("EmitterTool:SpritePanel:SetSingle", { name: nm });
      } else if (mode === "multi") {
        const names = Array.isArray(finalES.selectedSprites)
          ? finalES.selectedSprites.slice()
          : [];
        emit("EmitterTool:SpritePanel:SetMulti", { names });
      } else if (mode === "anim") {
        const frames = Array.isArray(finalES.selectedAnimFrames)
          ? finalES.selectedAnimFrames.slice()
          : [];
        const fps = Number(finalES.animFps || 12);
        emit("EmitterTool:SpritePanel:SetAnim", { frames, fps });
      }
    } catch {}
  }

  if (data.ParticlesPanel && typeof data.ParticlesPanel === "object") {
    usedPanelHeaders = true;
    const pp = data.ParticlesPanel;
    const before =
      env?.effectSettings && typeof env.effectSettings === "object"
        ? env.effectSettings
        : finalES;
    const patch = {};
    const num = (v) => (Number.isFinite(+v) ? +v : undefined);
    const str = (v) => (typeof v === "string" ? v : undefined);
    const bool = (v) => (typeof v === "boolean" ? v : undefined);
    if (num(pp.pps) != null) patch.pps = num(pp.pps);
    if (num(pp.maxAlive) != null)
      patch.maxAlive = Math.max(0, Math.floor(num(pp.maxAlive)));
    if (num(pp.speedMin) != null) patch.speedMin = num(pp.speedMin);
    if (num(pp.speedMax) != null) patch.speedMax = num(pp.speedMax);
    if (str(pp.lifespanMode)) patch.lifespanMode = pp.lifespanMode;
    if (num(pp.lifeMinS) != null) patch.lifeMinS = num(pp.lifeMinS);
    if (num(pp.lifeMaxS) != null) patch.lifeMaxS = num(pp.lifeMaxS);
    if (num(pp.rangeMeters) != null)
      patch.rangeMeters = num(pp.rangeMeters);
    if (num(pp.targetKillDistMeters) != null)
      patch.targetKillDistMeters = num(pp.targetKillDistMeters);
    if (num(pp.alphaStart) != null)
      patch.alphaStart = Math.max(
        0,
        Math.min(1, num(pp.alphaStart))
      );
    if (num(pp.alphaEnd) != null)
      patch.alphaEnd = Math.max(
        0,
        Math.min(1, num(pp.alphaEnd))
      );
    if (num(pp.scaleStart) != null)
      patch.scaleStart = num(pp.scaleStart);
    if (num(pp.scaleEnd) != null)
      patch.scaleEnd = num(pp.scaleEnd);
    if (str(pp.blendMode)) patch.blendMode = pp.blendMode;
    if (bool(pp.tintEnabled) != null)
      patch.tintEnabled = !!pp.tintEnabled;
    if (str(pp.tintStart)) patch.tintStart = pp.tintStart;
    if (str(pp.tintEnd)) patch.tintEnd = pp.tintEnd;
    if (str(pp.tintEase)) patch.tintEase = pp.tintEase;
    if (str(pp.facingMode)) patch.facingMode = pp.facingMode;
    if (num(pp.facingDegFromOrigin) != null)
      patch.facingDegFromOrigin = num(pp.facingDegFromOrigin);
    const mergedPreview = { ...before, ...patch };
    if (
      Number.isFinite(+mergedPreview.speedMin) &&
      Number.isFinite(+mergedPreview.speedMax)
    ) {
      if (+mergedPreview.speedMin > +mergedPreview.speedMax) {
        const a = +mergedPreview.speedMin;
        const b = +mergedPreview.speedMax;
        patch.speedMin = Math.min(a, b);
        patch.speedMax = Math.max(a, b);
      }
    }
    if (
      Number.isFinite(+mergedPreview.lifeMinS) &&
      Number.isFinite(+mergedPreview.lifeMaxS)
    ) {
      if (+mergedPreview.lifeMinS > +mergedPreview.lifeMaxS) {
        const a = +mergedPreview.lifeMinS;
        const b = +mergedPreview.lifeMaxS;
        patch.lifeMinS = Math.min(a, b);
        patch.lifeMaxS = Math.max(a, b);
      }
    }
    mergeES(patch);
  }

  if (data.OrbitPanel && typeof data.OrbitPanel === "object") {
    usedPanelHeaders = true;
    const op = data.OrbitPanel;
    const before =
      env?.effectSettings && typeof env.effectSettings === "object"
        ? env.effectSettings
        : finalES;
    const num = (v) => (Number.isFinite(+v) ? +v : undefined);
    const bool = (v) => (typeof v === "boolean" ? v : undefined);
    const str = (v) => (typeof v === "string" ? v : undefined);
    const patch = {};
    if (bool(op.orbitEnabled) != null)
      patch.orbitEnabled = !!op.orbitEnabled;
    const incomingDir =
      op.orbitDirection === "cw" || op.orbitDirection === "ccw"
        ? op.orbitDirection
        : undefined;
    const absDps = num(op.orbitSpeedAbsDps);
    const signed = num(op.orbitSpeedDegPerS);
    let finalDirection =
      incomingDir ?? (before.orbitDirection === "cw" ? "cw" : "ccw");
    let finalAbs = Number.isFinite(absDps)
      ? absDps
      : Number.isFinite(signed)
      ? Math.abs(signed)
      : Number.isFinite(+before.orbitSpeedAbsDps)
      ? +before.orbitSpeedAbsDps
      : Number.isFinite(+before.orbitSpeedDegPerS)
      ? Math.abs(+before.orbitSpeedDegPerS)
      : 90;
    let finalSigned = Number.isFinite(signed)
      ? signed
      : finalDirection === "ccw"
      ? +finalAbs
      : -finalAbs;
    if (Number.isFinite(absDps) && Number.isFinite(signed)) {
      const signFromDir =
        (incomingDir || finalDirection) === "ccw" ? 1 : -1;
      finalSigned = signFromDir * Math.abs(absDps);
      finalAbs = Math.abs(absDps);
      finalDirection = incomingDir || (finalSigned >= 0 ? "ccw" : "cw");
    }
    patch.orbitDirection = finalDirection;
    patch.orbitSpeedAbsDps = Math.abs(finalAbs);
    patch.orbitSpeedDegPerS = finalSigned;
    if (bool(op.orbitLockRadii) != null)
      patch.orbitLockRadii = !!op.orbitLockRadii;
    const rmin = num(op.orbitRadiusMinM);
    const rmax = num(op.orbitRadiusMaxM);
    if (rmin != null) patch.orbitRadiusMinM = rmin;
    if (rmax != null) patch.orbitRadiusMaxM = rmax;
    if (str(op.orbitShape))
      patch.orbitShape =
        op.orbitShape === "ellipse" ? "ellipse" : "circle";
    if (num(op.orbitEllipseRxM) != null)
      patch.orbitEllipseRxM = num(op.orbitEllipseRxM);
    if (num(op.orbitEllipseRyM) != null)
      patch.orbitEllipseRyM = num(op.orbitEllipseRyM);
    if (num(op.orbitEllipseTiltDeg) != null)
      patch.orbitEllipseTiltDeg = num(op.orbitEllipseTiltDeg);
    if (str(op.orbitStartPhaseMode))
      patch.orbitStartPhaseMode =
        op.orbitStartPhaseMode === "aligned"
          ? "aligned"
          : "random";
    if (num(op.orbitStartPhaseJitterDeg) != null)
      patch.orbitStartPhaseJitterDeg = Math.max(
        0,
        Math.min(360, num(op.orbitStartPhaseJitterDeg))
      );
    if (str(op.orbitApplyMode))
      patch.orbitApplyMode =
        op.orbitApplyMode === "percent" ? "percent" : "all";
    if (num(op.orbitApplyPercent) != null)
      patch.orbitApplyPercent = Math.max(
        0,
        Math.min(100, num(op.orbitApplyPercent))
      );
    patch.orbitMode = "aroundOrigin";
    patch.orbitGroupTransform = false;
    const mergedPreview = { ...before, ...patch };
    if (
      Number.isFinite(+mergedPreview.orbitRadiusMinM) &&
      Number.isFinite(+mergedPreview.orbitRadiusMaxM)
    ) {
      if (+mergedPreview.orbitRadiusMinM > +mergedPreview.orbitRadiusMaxM) {
        const a = +mergedPreview.orbitRadiusMinM;
        const b = +mergedPreview.orbitRadiusMaxM;
        patch.orbitRadiusMinM = Math.min(a, b);
        patch.orbitRadiusMaxM = Math.max(a, b);
      }
    }
    mergeES(patch);
    try {
      if (finalES.orbitEnabled) {
        emit("EmitterTool:ParticlesConfig", {
          orbitEnabled: false,
        });
        emit("EmitterTool:ParticlesConfig", {
          ...finalES,
          orbitEnabled: true,
        });
      }
    } catch {}
  }

  if (data.RotationPanel && typeof data.RotationPanel === "object") {
    usedPanelHeaders = true;
    const rp = data.RotationPanel;
    const before =
      env?.effectSettings && typeof env.effectSettings === "object"
        ? env.effectSettings
        : finalES;
    const num = (v) => (Number.isFinite(+v) ? +v : undefined);
    const bool = (v) => (typeof v === "boolean" ? v : undefined);
    const patch = {};
    if (bool(rp.rotateEnabled) != null)
      patch.rotateEnabled = !!rp.rotateEnabled;
    const incomingDir =
      rp.rotateDirection === "cw" || rp.rotateDirection === "ccw"
        ? rp.rotateDirection
        : undefined;
    const absDps = num(rp.rotateSpeedAbsDps);
    const signed = num(rp.rotateSpeedDegPerS);
    let finalDirection =
      incomingDir ?? (before.rotateDirection === "cw" ? "cw" : "ccw");
    let finalAbs = Number.isFinite(absDps)
      ? absDps
      : Number.isFinite(signed)
      ? Math.abs(signed)
      : Number.isFinite(+before.rotateSpeedAbsDps)
      ? +before.rotateSpeedAbsDps
      : Number.isFinite(+before.rotateSpeedDegPerS)
      ? Math.abs(+before.rotateSpeedDegPerS)
      : 90;
    let finalSigned = Number.isFinite(signed)
      ? signed
      : finalDirection === "ccw"
      ? +finalAbs
      : -finalAbs;
    patch.rotateDirection = finalDirection;
    patch.rotateSpeedAbsDps = Math.abs(finalAbs);
    patch.rotateSpeedDegPerS = finalSigned;
    mergeES(patch);
    try {
      if (finalES.rotateEnabled) {
        emit("EmitterTool:ParticlesConfig", {
          rotateEnabled: false,
        });
        emit("EmitterTool:ParticlesConfig", {
          ...finalES,
          rotateEnabled: true,
        });
      }
    } catch {}
  }

  if (data.GravityPanel && typeof data.GravityPanel === "object") {
    usedPanelHeaders = true;
    const gp = data.GravityPanel;
    const num = (v) => (Number.isFinite(+v) ? +v : undefined);
    const bool = (v) => (typeof v === "boolean" ? v : undefined);
    const patch = {};
    if (bool(gp.gravityEnabled) != null)
      patch.gravityEnabled = !!gp.gravityEnabled;
    if (num(gp.gravitySpeed) != null)
      patch.gravitySpeed = Math.max(
        -10,
        Math.min(10, num(gp.gravitySpeed))
      );
    if (num(gp.gravityLingerS) != null)
      patch.gravityLingerS = Math.max(
        0,
        Math.min(3, num(gp.gravityLingerS))
      );
    mergeES(patch);
    try {
      if (finalES.gravityEnabled) {
        emit("EmitterTool:ParticlesConfig", {
          gravityEnabled: false,
        });
        emit("EmitterTool:ParticlesConfig", {
          ...finalES,
          gravityEnabled: true,
        });
      }
    } catch {}
  }

  {
    const tpRaw =
      (data.TrailPanel &&
        typeof data.TrailPanel === "object" &&
        data.TrailPanel) ||
      (data.TrailsPanel &&
        typeof data.TrailsPanel === "object" &&
        data.TrailsPanel) ||
      (data.TrailesPanel &&
        typeof data.TrailesPanel === "object" &&
        data.TrailesPanel) ||
      (data.Trailes &&
        typeof data.Trailes === "object" &&
        data.Trailes) ||
      null;
    if (tpRaw) {
      usedPanelHeaders = true;
      const tp = tpRaw;
      const before =
        env?.effectSettings && typeof env.effectSettings === "object"
          ? env.effectSettings
          : finalES;
      const num = (v) => (Number.isFinite(+v) ? +v : undefined);
      const str = (v) => (typeof v === "string" ? v : undefined);
      const bool = (v) => (typeof v === "boolean" ? v : undefined);
      const patch = {};
      const on = bool(tp.trailEnabled) ?? bool(tp.enabled);
      if (on != null) patch.trailEnabled = !!on;
      const mode = str(tp.trailMode) ?? str(tp.mode);
      if (mode) patch.trailMode = mode;
      const a0 = num(tp.trailAlphaStart) ?? num(tp.alphaStart);
      if (a0 != null)
        patch.trailAlphaStart = Math.max(0, Math.min(1, a0));
      const a1 = num(tp.trailAlphaEnd) ?? num(tp.alphaEnd);
      if (a1 != null)
        patch.trailAlphaEnd = Math.max(0, Math.min(1, a1));
      const w =
        num(tp.ribbonWidthPx) ??
        num(tp.widthPx) ??
        num(tp.trailWidthPx);
      if (w != null) patch.ribbonWidthPx = Math.max(0, w);
      const len = num(tp.trailLength) ?? num(tp.length);
      if (len != null)
        patch.trailLength = Math.max(1, Math.floor(len));
      const space =
        num(tp.trailSpacingMs) ?? num(tp.spacingMs);
      if (space != null)
        patch.trailSpacingMs = Math.max(0, Math.floor(space));
      const bm =
        str(tp.trailBlendMode) ?? str(tp.blendMode);
      if (bm) patch.trailBlendMode = bm;
      const useSrc =
        bool(tp.trailUseSpriteSource) ?? bool(tp.useSpriteSource);
      if (useSrc != null) patch.trailUseSpriteSource = !!useSrc;
      const texKey =
        str(tp.trailTextureKey) ?? str(tp.textureKey);
      if (texKey) patch.trailTextureKey = texKey;
      const s0 = num(tp.trailScaleStart) ?? num(tp.scaleStart);
      if (s0 != null) patch.trailScaleStart = Math.max(0, s0);
      const s1 = num(tp.trailScaleEnd) ?? num(tp.scaleEnd);
      if (s1 != null) patch.trailScaleEnd = Math.max(0, s1);
      const mergedPreview = { ...before, ...patch };
      if (
        Number.isFinite(+mergedPreview.trailScaleStart) &&
        Number.isFinite(+mergedPreview.trailScaleEnd)
      ) {
        if (+mergedPreview.trailScaleStart < 0)
          patch.trailScaleStart = 0;
        if (+mergedPreview.trailScaleEnd < 0)
          patch.trailScaleEnd = 0;
      }
      mergeES(patch);
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:ParticlesConfig", {
            detail: finalES,
          })
        );
      } catch {}
    }
  }

  if (data.GlowPanel && typeof data.GlowPanel === "object") {
    usedPanelHeaders = true;
    const gl = data.GlowPanel;
    const before =
      env?.effectSettings && typeof env.effectSettings === "object"
        ? env.effectSettings
        : finalES;
    const num = (v) => (Number.isFinite(+v) ? +v : undefined);
    const bool = (v) => (typeof v === "boolean" ? v : undefined);
    const str = (v) => (typeof v === "string" ? v : undefined);
    const patch = {};
    if (bool(gl.glowEnabled) != null)
      patch.glowEnabled = !!gl.glowEnabled;
    if (num(gl.glowPercent) != null)
      patch.glowPercent = Math.max(
        0,
        Math.min(100, num(gl.glowPercent))
      );
    if (num(gl.glowIntensity) != null)
      patch.glowIntensity = Math.max(
        0,
        Math.min(1, num(gl.glowIntensity))
      );
    const reachPx = num(gl.glowReachPx);
    const reachM = num(gl.glowReachM);
    if (Number.isFinite(reachPx) && reachPx > 0) {
      patch.glowReachPx = Math.max(0, reachPx);
      patch.glowReachM = 0;
    } else if (Number.isFinite(reachM) && reachM > 0) {
      patch.glowReachM = Math.max(0, reachM);
      patch.glowReachPx = 0;
    } else {
      if (
        before.glowReachPx == null &&
        before.glowReachM == null
      ) {
        patch.glowReachM = 10;
        patch.glowReachPx = 0;
      }
    }
    if (num(gl.glowDurationS) != null)
      patch.glowDurationS = Math.max(
        0,
        num(gl.glowDurationS)
      );
    if (bool(gl.tintEnabled) != null)
      patch.tintEnabled = !!gl.tintEnabled;
    if (str(gl.tintStart)) patch.tintStart = gl.tintStart;
    if (str(gl.tintEnd)) patch.tintEnd = gl.tintEnd;
    mergeES(patch);
    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:GlowConfig", {
          detail: finalES,
        })
      );
    } catch {}
  }

  if (data.CollisionPanel && typeof data.CollisionPanel === "object") {
    usedPanelHeaders = true;
    const cp = data.CollisionPanel;
    const before =
      env?.effectSettings && typeof env.effectSettings === "object"
        ? env.effectSettings
        : finalES;
    const num = (v) => (Number.isFinite(+v) ? +v : undefined);
    const bool = (v) => (typeof v === "boolean" ? v : undefined);
    const str = (v) => (typeof v === "string" ? v : undefined);
    const patch = {};
    if (bool(cp.useWalls) != null)
      patch.useWalls = !!cp.useWalls;
    if (str(cp.wallResponse)) patch.wallResponse = cp.wallResponse;
    if (bool(cp.useTokenCollision) != null)
      patch.useTokenCollision = !!cp.useTokenCollision;
    if (str(cp.tokenResponse)) patch.tokenResponse = cp.tokenResponse;
    if (bool(cp.collisionEffectEnabled) != null)
      patch.collisionEffectEnabled = !!cp.collisionEffectEnabled;
    if (str(cp.collisionSprite))
      patch.collisionSprite = cp.collisionSprite;
    if (num(cp.collisionScale) != null)
      patch.collisionScale = Math.max(0, num(cp.collisionScale));
    if (num(cp.collisionAlpha) != null)
      patch.collisionAlpha = Math.max(
        0,
        Math.min(1, num(cp.collisionAlpha))
      );
    if (num(cp.collisionLifespanMs) != null)
      patch.collisionLifespanMs = Math.max(
        0,
        Math.floor(num(cp.collisionLifespanMs))
      );
    if (str(cp.collisionFxRotationMode))
      patch.collisionFxRotationMode = cp.collisionFxRotationMode;
    mergeES(patch);
    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:CollisionSettings", {
          detail: finalES,
        })
      );
    } catch {}
  }

  {
    const parentES = data?.parent?.effectSettings;
    if (
      parentES &&
      (Array.isArray(parentES.subEmitters) ||
        Number.isFinite(parentES.subEmittersParentIndex))
    ) {
      const subs = Array.isArray(parentES.subEmitters)
        ? parentES.subEmitters.slice()
        : finalES.subEmitters;
      const pIdx = Number.isFinite(
        parentES.subEmittersParentIndex
      )
        ? parentES.subEmittersParentIndex
        : finalES.subEmittersParentIndex;
      mergeES({
        subEmitters: subs,
        subEmittersEnabled: Array.isArray(subs)
          ? subs.length > 0
          : !!finalES.subEmittersEnabled,
        subEmittersParentIndex: pIdx,
      });
      emitSubEmitterRules(subs, "import.parent.effectSettings");
    }
  }

  if (
    data.EmitterPanel ||
    data.TargetingPanel ||
    data.SpritePanel ||
    data.ParticlesPanel ||
    data.OrbitPanel ||
    data.RotationPanel ||
    data.GravityPanel ||
    data.CollisionPanel ||
    data.GlowPanel ||
    data.TrailPanel ||
    data.TrailsPanel ||
    data.TrailesPanel ||
    data.Trailes
  ) {
    if (Array.isArray(data.layers)) setLayers?.(data.layers);
    setActiveLayerIndex?.(-1);
    try {
      window.dispatchEvent(
        new CustomEvent("EmitterTool:UI:PresetApplied", {
          detail: { preset: data },
        })
      );
      window.dispatchEvent(
        new CustomEvent("EmitterTool:Engine:ApplyAll", {
          detail: { from: "import:panel-headers" },
        })
      );
      emit("EmitterTool:ScopeChanged", {
        ts: performance.now(),
        type: "parent",
        scopeType: "parent",
        scopeId: "parent",
      });
    } catch {}
    try {
      const src = bestTrailSource(data);
      const trailPatch = pickTrailPatch(src);
      if (Object.keys(trailPatch).length) {
        setEffectSettings?.((cur) => ({
          ...(cur || {}),
          ...trailPatch,
        }));
        finalES = { ...(finalES || {}), ...trailPatch };
      }
      emit("EmitterTool:ParticlesConfig", finalES);
      emit("EmitterTool:GlowConfig", finalES);
      emit("EmitterTool:CollisionSettings", finalES);
      if (Array.isArray(finalES.subEmitters))
        emitSubEmitterRules(
          finalES.subEmitters,
          "import.finalES"
        );
      if (typeof window.__ET_REHYDRATE === "function") {
        window.__ET_REHYDRATE({
          reason: "PresetApplied(panel-headers)",
          effectSettings: finalES,
          preset: data,
        });
      }
    } catch {}
    return;
  }

  if (typeof data.name === "string") setEffectName?.(data.name);
  if (Number.isFinite(+data.pxPerMeter))
    setPxPerMeter?.(+data.pxPerMeter);
  if (typeof data.showGrid === "boolean")
    setShowGrid?.(!!data.showGrid);
  if (
    typeof data.effectKey === "string" &&
    effects?.[data.effectKey]
  ) {
    setEffectKey?.(data.effectKey);
  }

  const parent = data.parent || {};
  if (parent && typeof parent === "object") {
    if (typeof parent.selectedSprite === "string") {
      setSelectedSprite?.(parent.selectedSprite);
    }
    if (
      parent.effectSettings &&
      typeof parent.effectSettings === "object"
    ) {
      mergeES(parent.effectSettings);
      if (
        Array.isArray(parent.effectSettings.subEmitters) ||
        Number.isFinite(
          parent.effectSettings.subEmittersParentIndex
        )
      ) {
        const subs = Array.isArray(parent.effectSettings.subEmitters)
          ? parent.effectSettings.subEmitters.slice()
          : finalES.subEmitters;
        const pIdx = Number.isFinite(
          parent.effectSettings.subEmittersParentIndex
        )
          ? parent.effectSettings.subEmittersParentIndex
          : finalES.subEmittersParentIndex;
        mergeES({
          subEmitters: subs,
          subEmittersEnabled: Array.isArray(subs)
            ? subs.length > 0
            : !!finalES.subEmittersEnabled,
          subEmittersParentIndex: pIdx,
        });
        emitSubEmitterRules(
          subs,
          "import.legacy.parent.effectSettings"
        );
      }
    }
    if (typeof parent.emitterShape === "string") {
      setEmitterShape?.(parent.emitterShape);
    }
    if (
      parent.shapeSettings &&
      typeof parent.shapeSettings === "object"
    ) {
      setShapeSettings?.((cur) => ({
        ...(cur || {}),
        ...parent.shapeSettings,
      }));
    }
    if (typeof parent.wallsEnabled === "boolean")
      setWallsEnabled?.(parent.wallsEnabled);
    if (typeof parent.tokenEnabled === "boolean")
      setTokenEnabled?.(parent.tokenEnabled);
    if (typeof parent.infinite === "boolean")
      setInfinite?.(!!parent.infinite);
    if (Number.isFinite(+parent.infiniteRate))
      setInfiniteRate?.(+parent.infiniteRate);

    const legacyTargeting =
      parent.targeting ?? (parent.effectSettings && parent.effectSettings.targeting) ?? null;
    if (legacyTargeting && typeof legacyTargeting === "object") {
      const mode = (legacyTargeting.mode || "").toLowerCase();
      const rangeM = Number(legacyTargeting.rangeM);
      const headSpeedMps = Number(legacyTargeting.headSpeedMps);
      try {
        if (mode)
          localStorage.setItem(
            "EmitterTool:targeting:mode",
            mode
          );
      } catch {}
      try {
        if (Number.isFinite(rangeM))
          localStorage.setItem(
            "EmitterTool:targeting:rangeM",
            String(rangeM)
          );
      } catch {}
      try {
        if (Number.isFinite(headSpeedMps))
          localStorage.setItem(
            "EmitterTool:targeting:headSpeedMps",
            String(headSpeedMps)
          );
      } catch {}
      try {
        window.dispatchEvent(
          new CustomEvent("EmitterTool:Targeting:Hydrate", {
            detail: {
              mode,
              rangeM,
              headSpeedMps,
              targeting: legacyTargeting,
            },
          })
        );
      } catch {}
    }
  }

  if (Array.isArray(data.layers)) {
    const nextLayers = data.layers.map((L, i) => ({
      name: L?.name || `Layer ${i + 1}`,
      enabled: L?.enabled !== false,
      effectKey: L?.effectKey || data.effectKey || effectKey,
      selectedSprite:
        L?.selectedSprite ?? selectedSprite,
      effectSettings: L?.effectSettings || finalES || {},
      emitterShape: L?.emitterShape || emitterShape,
      shapeSettings: L?.shapeSettings || {},
      infinite: !!L?.infinite,
      infiniteRate: Number(L?.infiniteRate ?? 30),
      aboveParent: !!L?.aboveParent,
    }));
    setLayers?.(nextLayers);
  }

  setActiveLayerIndex?.(-1);

  try {
    window.dispatchEvent(
      new CustomEvent("EmitterTool:UI:PresetApplied", {
        detail: { preset: data },
      })
    );
  } catch {}

  try {
    const src = bestTrailSource(data);
    const trailPatch = pickTrailPatch(src);
    if (Object.keys(trailPatch).length) {
      setEffectSettings?.((cur) => ({
        ...(cur || {}),
        ...trailPatch,
      }));
      finalES = { ...(finalES || {}), ...trailPatch };
    }
    emit("EmitterTool:ParticlesConfig", finalES);
    emit("EmitterTool:GlowConfig", finalES);
    emit("EmitterTool:CollisionSettings", finalES);
    if (Array.isArray(finalES.subEmitters))
      emitSubEmitterRules(
        finalES.subEmitters,
        "import.legacy.finalES"
      );
  } catch {}
}

export function applyPresetFromText(text, env) {
  const raw = parseExportModuleText(text);
  const data = coerceSnapshotForLegacyImport(raw);
  applyImportedEffectObject(data, env);
  return data;
}
